/**
 * 18+ 内容年龄确认弹窗(ZKSN-246)
 *
 * 后端在 /content/getContentDetail 返回 ageGate.required = true 时,
 * 若本地无有效确认记录则弹出年龄确认弹窗。
 * - 点击 Yes:本地持久化确认记录(带 policyVersion),后续访问 18+ 内容不再弹出
 * - 点击 No:返回上一页且弹窗保持,不写入任何记录;目标页会重新校验决定弹窗去留
 * - 后端升级 policyVersion 后,旧确认记录失效,重新弹窗
 */
import { ref } from 'vue'
import { STORAGE_AGE_GATE_CONFIRMED } from '@/constants'

type AgeGate = NonNullable<GetContentGetcontentdetailResponse['data']['ageGate']>

interface AgeGateConfirmedRecord {
  policyVersion: number
  confirmedAt: number
}

// 模块级单例:弹窗全局唯一,由 App.vue 中的 BizAgeGateModal 统一展示
const showAgeGate = ref(false)
let pendingPolicyVersion = 0

function getConfirmedRecord(): AgeGateConfirmedRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_AGE_GATE_CONFIRMED)
    return raw ? JSON.parse(raw) : null
  }
  catch {
    return null
  }
}

export function useAgeGate() {
  /**
   * 内容详情返回后调用,是弹窗显隐的唯一权威:
   * 命中条件弹出;不命中则关闭(点击 No 跨页保持的弹窗由此在非 18+ 页面自愈关闭)
   */
  function checkAgeGate(ageGate?: AgeGate) {
    const policyVersion = ageGate?.policyVersion ?? 0
    const confirmed = getConfirmedRecord()
    // 确认记录仅对做出确认时的策略版本有效:版本不一致(升级或回滚)都重新弹窗
    const passed = !!confirmed && Number(confirmed.policyVersion ?? 0) === policyVersion

    if (!ageGate?.required || passed) {
      showAgeGate.value = false
      return
    }

    pendingPolicyVersion = policyVersion
    showAgeGate.value = true
  }

  /**
   * 点击 Yes:记录确认状态并关闭弹窗
   */
  function confirmAgeGate() {
    const record: AgeGateConfirmedRecord = {
      policyVersion: pendingPolicyVersion,
      confirmedAt: Date.now(),
    }
    // 写入失败(隐私模式/配额满)时仍关闭弹窗,下次访问会重新弹出
    try {
      localStorage.setItem(STORAGE_AGE_GATE_CONFIRMED, JSON.stringify(record))
    }
    catch {}
    showAgeGate.value = false
  }

  return {
    showAgeGate,
    checkAgeGate,
    confirmAgeGate,
  }
}
